/**
 * Unit-system aware formatting and parsing. All values held in stores and sent
 * to the server are SI (kg, cm, m, s); conversion happens only here, at the
 * input and display boundary.
 */

export type UnitSystem = 'metric' | 'imperial'

export type Quantity = 'mass' | 'length' | 'distance' | 'duration'

export const DEFAULT_UNIT_SYSTEM: UnitSystem = 'metric'

const KG_PER_LB = 0.45359237
const CM_PER_IN = 2.54
const M_PER_MI = 1609.344
const M_PER_KM = 1000

type UnitSpec = {
   label: string
   /** Multiply an SI value by this to get the display value. */
   fromSi: number
}

const UNIT_TABLE: Record<Exclude<Quantity, 'duration'>, Record<UnitSystem, UnitSpec>> = {
   mass: {
      metric: { label: 'kg', fromSi: 1 },
      imperial: { label: 'lb', fromSi: 1 / KG_PER_LB },
   },
   length: {
      metric: { label: 'cm', fromSi: 1 },
      imperial: { label: 'in', fromSi: 1 / CM_PER_IN },
   },
   distance: {
      metric: { label: 'km', fromSi: 1 / M_PER_KM },
      imperial: { label: 'mi', fromSi: 1 / M_PER_MI },
   },
}

const EXAMPLE_VALUES: Record<
   Exclude<Quantity, 'duration'>,
   Record<UnitSystem, string>
> = {
   mass: { metric: '72.4', imperial: '160' },
   length: { metric: '172.5', imperial: '68' },
   distance: { metric: '5.0', imperial: '3.1' },
}

/**
 * Returns a round example value for input placeholders in the display unit.
 *
 * @param quantity - The quantity kind.
 * @param unitSystem - The active unit system.
 * @returns The example string (no unit label).
 */
export function getExampleValue(
   quantity: Exclude<Quantity, 'duration'>,
   unitSystem: UnitSystem,
) {
   return EXAMPLE_VALUES[quantity][unitSystem]
}

/**
 * Normalizes a stored or remote unit-system value.
 *
 * @param value - The candidate value.
 * @returns The unit system, defaulting to metric.
 */
export function normalizeUnitSystem(value: unknown): UnitSystem {
   return value === 'imperial' ? 'imperial' : DEFAULT_UNIT_SYSTEM
}

/**
 * Returns the display unit label for a quantity (e.g. `kg`, `lb`, `mi`).
 *
 * @param quantity - The quantity kind.
 * @param unitSystem - The active unit system.
 * @returns The unit label.
 */
export function getUnitLabel(
   quantity: Exclude<Quantity, 'duration'>,
   unitSystem: UnitSystem,
) {
   return UNIT_TABLE[quantity][unitSystem].label
}

/**
 * Converts an SI value to the display unit of the given system.
 *
 * @param quantity - The quantity kind.
 * @param siValue - The value in SI units.
 * @param unitSystem - The active unit system.
 * @returns The value in display units.
 */
function toDisplayValue(
   quantity: Exclude<Quantity, 'duration'>,
   siValue: number,
   unitSystem: UnitSystem,
) {
   return siValue * UNIT_TABLE[quantity][unitSystem].fromSi
}

/**
 * Converts a display-unit value back to SI.
 *
 * @param quantity - The quantity kind.
 * @param displayValue - The value in display units.
 * @param unitSystem - The active unit system.
 * @returns The value in SI units.
 */
function fromDisplayValue(
   quantity: Exclude<Quantity, 'duration'>,
   displayValue: number,
   unitSystem: UnitSystem,
) {
   return displayValue / UNIT_TABLE[quantity][unitSystem].fromSi
}

/**
 * Formats an SI value in the display unit, optionally with its unit label.
 *
 * @param quantity - The quantity kind.
 * @param siValue - The value in SI units.
 * @param unitSystem - The active unit system.
 * @param options - `digits` (default 1), `withUnit` (default true), and an optional
 *   `locale` for locale-aware number formatting (falls back to `toFixed`).
 * @returns The formatted string.
 */
export function formatQuantity(
   quantity: Exclude<Quantity, 'duration'>,
   siValue: number,
   unitSystem: UnitSystem,
   options: { digits?: number; withUnit?: boolean; locale?: string } = {},
) {
   const digits = options.digits ?? 1
   const displayValue = toDisplayValue(quantity, siValue, unitSystem)
   const value =
      options.locale ?
         displayValue.toLocaleString(options.locale, {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
         })
      :  displayValue.toFixed(digits)
   return options.withUnit === false ?
         value
      :  `${value} ${getUnitLabel(quantity, unitSystem)}`
}

/**
 * Formats an SI value for an input field in the display unit (no unit label,
 * no decimals when whole).
 *
 * @param quantity - The quantity kind.
 * @param siValue - The value in SI units, or null.
 * @param unitSystem - The active unit system.
 * @param digits - Maximum fraction digits (default 1).
 * @returns The input string, or empty when null.
 */
export function formatQuantityInput(
   quantity: Exclude<Quantity, 'duration'>,
   siValue: number | null,
   unitSystem: UnitSystem,
   digits = 1,
) {
   if (typeof siValue !== 'number' || !Number.isFinite(siValue)) {
      return ''
   }
   const value = toDisplayValue(quantity, siValue, unitSystem)
   const rounded = Number(value.toFixed(digits))
   return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(digits)
}

/**
 * Parses a typed value in display units and returns it in SI.
 *
 * @param quantity - The quantity kind.
 * @param input - The raw input string.
 * @param unitSystem - The active unit system.
 * @returns The SI value, or null when not a finite number.
 */
export function parseQuantity(
   quantity: Exclude<Quantity, 'duration'>,
   input: string,
   unitSystem: UnitSystem,
): number | null {
   const parsed = Number.parseFloat(input.replace(',', '.'))
   if (!Number.isFinite(parsed)) {
      return null
   }
   return fromDisplayValue(quantity, parsed, unitSystem)
}

/**
 * Formats a duration in seconds as `m:ss` or `h:mm:ss`.
 *
 * @param seconds - The duration in seconds.
 * @returns The formatted string.
 */
export function formatDurationSeconds(seconds: number) {
   const total = Math.max(0, Math.round(seconds))
   const hours = Math.floor(total / 3600)
   const minutes = Math.floor((total % 3600) / 60)
   const secs = total % 60
   const mmss = `${hours > 0 ? String(minutes).padStart(2, '0') : minutes}:${String(secs).padStart(2, '0')}`
   return hours > 0 ? `${hours}:${mmss}` : mmss
}

/**
 * Parses a typed duration into seconds. Accepts `m:ss` / `h:mm:ss`, but also the
 * separators numeric keypads offer instead of a colon (`.` `,` `;` `-` space),
 * and bare digits read like a microwave: `45` → 45 s, `1012` → 10:12,
 * `13500` → 1:35:00.
 *
 * @param input - The raw input string.
 * @returns The duration in seconds, or null when invalid.
 */
export function parseDurationSeconds(input: string): number | null {
   const trimmed = input.trim()
   const parts =
      /^\d+$/.test(trimmed) ? splitBareDigits(trimmed) : trimmed.split(/[:.,;\-\s]+/)
   if (
      parts.length === 0 ||
      parts.length > 3 ||
      parts.some(part => !/^\d+$/.test(part))
   ) {
      return null
   }
   const seconds = parts.map(Number).reduce((sum, value) => sum * 60 + value, 0)
   return seconds > 0 ? seconds : null
}

/**
 * Splits a digit-only duration into `[h?, mm, ss]` groups from the right.
 *
 * @param digits - The digit string.
 * @returns The groups, or `[digits]` when it is short enough to be plain seconds.
 */
function splitBareDigits(digits: string): string[] {
   if (digits.length <= 2) {
      return [digits]
   }
   if (digits.length <= 4) {
      return [digits.slice(0, -2), digits.slice(-2)]
   }
   if (digits.length <= 6) {
      return [digits.slice(0, -4), digits.slice(-4, -2), digits.slice(-2)]
   }
   return [digits, digits, digits, digits]
}

const MAX_DURATION_DIGITS = 6

/**
 * Reformats typed duration input so digits fill from the right, like a microwave
 * timer: `5` → `0:05`, `1012` → `10:12`, `13000` → `1:30:00`. Every non-digit is
 * dropped, so the user never has to find a colon on a numeric keypad.
 *
 * @param input - The raw input value, possibly already formatted.
 * @returns The formatted duration, or an empty string when no digits remain.
 */
export function formatDurationDigits(input: string) {
   const digits = input
      .replace(/\D/g, '')
      .replace(/^0+/, '')
      .slice(0, MAX_DURATION_DIGITS)
   if (!digits) {
      return ''
   }
   const padded = digits.padStart(3, '0')
   const seconds = padded.slice(-2)
   const minutes = padded.slice(0, -2)
   if (minutes.length <= 2) {
      return `${Number(minutes)}:${seconds}`
   }
   return `${minutes.slice(0, -2)}:${minutes.slice(-2)}:${seconds}`
}

const KM_PER_MI = M_PER_MI / M_PER_KM

/**
 * Formats a pace (seconds per kilometre) as `m:ss /km` or `m:ss /mi`.
 *
 * @param secondsPerKm - The pace in SI form.
 * @param unitSystem - The active unit system.
 * @returns The formatted pace.
 */
export function formatPace(secondsPerKm: number, unitSystem: UnitSystem) {
   const perUnit = unitSystem === 'imperial' ? secondsPerKm * KM_PER_MI : secondsPerKm
   return `${formatDurationSeconds(perUnit)} /${getUnitLabel('distance', unitSystem)}`
}

/**
 * Formats a speed (km/h) as `km/h` or `mph`.
 *
 * @param kmPerHour - The speed in SI form.
 * @param unitSystem - The active unit system.
 * @param locale - Optional locale for number formatting.
 * @returns The formatted speed.
 */
export function formatSpeed(kmPerHour: number, unitSystem: UnitSystem, locale?: string) {
   const value = unitSystem === 'imperial' ? kmPerHour / KM_PER_MI : kmPerHour
   const number =
      locale ?
         value.toLocaleString(locale, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
         })
      :  value.toFixed(1)
   return `${number} ${unitSystem === 'imperial' ? 'mph' : 'km/h'}`
}
